/**
 * `DesktopLanguageSwitcher` — the language pill pinned to the top-right corner
 * at desktop width, where the menu sheet (and its language picker) is not
 * mounted. Each activation moves to the next language, Finnish → English →
 * Swedish → Finnish, as the prototype's pill does.
 *
 * It changes the language through the same `LanguagePort` as the menu sheet's
 * picker and keeps nothing of its own, so the two always agree and the D-208
 * no-browser-storage contract holds.
 *
 * The visible text is the active language's own name. The accessible name
 * leads with the action ("Vaihda kieli") and still contains that visible text,
 * so speech-input users can say what they see (WCAG 2.5.3).
 */

import type { JSX } from 'react';

import type { LanguageCode } from '../i18n/LanguagePort.js';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';

import { GlobeIcon } from './icons.js';

const NEXT_LANGUAGE: Record<LanguageCode, LanguageCode> = { fi: 'en', en: 'sv', sv: 'fi' };

// Above the page content (z-30), below dialogs (z-50); the back header starts
// below the top-bar height, so the pill never overlaps it.
const pillClasses =
  'fixed top-3 right-8 z-40 inline-flex items-center gap-2.5 rounded-full border ' +
  'border-border bg-background/90 px-stock-5 py-2 text-base font-medium text-foreground/80 shadow-sm ' +
  'backdrop-blur cursor-pointer transition-colors hover:bg-foreground/4 hover:text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export function DesktopLanguageSwitcher(): JSX.Element {
  const t = useTranslate();
  const { active, set } = useLanguage();

  return (
    <button
      type="button"
      className={pillClasses}
      onClick={() => {
        void set(NEXT_LANGUAGE[active]);
      }}
    >
      <GlobeIcon aria-hidden="true" className="h-stock-5 w-stock-5" />
      <span className="sr-only">{`${t('shell.desktopSidebar.languageSwitcher.label')}: `}</span>
      <span>{t(`shell.menu.languagePicker.${active}`)}</span>
    </button>
  );
}
